import React from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from 'recharts';
import { Calendar, CheckCircle2, AlertTriangle, FileText, DollarSign, ChevronDown } from 'lucide-react';
import { INVOICES, CASHFLOW_DATA } from '../constants';

const CashflowForecast: React.FC = () => {
  // Calculate stats from December 2025 invoices
  const totalAmount = INVOICES.reduce((acc, curr) => acc + curr.amount, 0);
  const totalInvoices = INVOICES.length;
  const paidCount = INVOICES.filter(i => i.status === 'Paid').length;
  const overdueCount = INVOICES.filter(i => i.status === 'Overdue').length;

  // Calendar generation for December 2025
  // Dec 1 2025 is a Monday. 31 Days.
  const daysInMonth = 31;
  const startingDay = 0; // 0 = Mon, 1 = Tue... based on prompt alignment usually Mon is first. 
  // Let's assume standard Mon-Sun grid.
  // Date(2025, 11, 1).getDay() is 1 (Monday). 
  
  const calendarDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const getInvoicesForDay = (day: number) => {
    return INVOICES.filter(inv => {
      const date = new Date(inv.dueDate);
      return date.getDate() === day && date.getMonth() === 11 && date.getFullYear() === 2025;
    });
  };

  const StatCard = ({ icon: Icon, label, value, color }: any) => (
    <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between shadow-sm">
      <div className="flex items-center space-x-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon size={24} />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-800 tracking-tight">{value}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in space-y-8 pb-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Cashflow Forecast</h1>
          <p className="text-slate-500">Monitor invoice schedules and payment projections</p>
        </div>
        <button className="flex items-center space-x-2 bg-white border border-slate-200 px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors text-sm font-medium shadow-sm">
          <Calendar size={16} />
          <span className="capitalize">December 2025</span>
          <ChevronDown size={14} className="text-slate-400" />
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          icon={DollarSign} 
          label="Total Amount" 
          value={`€ ${totalAmount.toLocaleString('de-DE', { minimumFractionDigits: 2 })}`} 
          color="bg-slate-50 text-slate-900" 
        />
        <StatCard 
          icon={FileText} 
          label="Total Invoices" 
          value={totalInvoices} 
          color="bg-slate-50 text-slate-900" 
        />
        <StatCard 
          icon={CheckCircle2} 
          label="Paid" 
          value={paidCount} 
          color="bg-emerald-50 text-emerald-600" 
        />
        <StatCard 
          icon={AlertTriangle} 
          label="Overdue" 
          value={overdueCount} 
          color="bg-red-50 text-red-600" 
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Monthly Invoice Schedule (Calendar) */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Calendar size={18} className="text-slate-500" />
              <h2 className="text-lg font-semibold text-slate-800">Monthly Invoice Schedule</h2>
            </div>
            <span className="text-xs text-slate-400">Invoices due each day of December 2025</span>
          </div>

          <div className="grid grid-cols-7 gap-px bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
              <div key={day} className="bg-slate-50 p-2 text-center text-xs font-medium text-slate-500">
                {day}
              </div>
            ))}
            
            {/* Empty cells for padding if month didn't start on Mon (Dec 2025 starts Mon, so 0 padding needed) */}
            
            {calendarDays.map(day => {
              const dayInvoices = getInvoicesForDay(day);
              const hasInvoices = dayInvoices.length > 0;
              const isToday = day === 10; // Mock "today" as the 10th for demo visual
              
              return (
                <div 
                  key={day} 
                  className={`bg-white min-h-[80px] p-2 flex flex-col justify-between hover:bg-slate-50 transition-colors relative ${isToday ? 'bg-slate-50/30' : ''}`}
                  >
                  <span className={`text-xs font-medium ${isToday ? 'text-slate-900' : 'text-slate-400'}`}>{day}</span>
                  {hasInvoices && (
                    <div className="mt-1 space-y-1">
                      {dayInvoices.map(inv => (
                        <div key={inv.id} className={`text-[9px] px-1.5 py-0.5 rounded border truncate ${
                          inv.status === 'Paid' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                          inv.status === 'Overdue' ? 'bg-red-100 text-red-700 border-red-200' :
                          'bg-blue-50 text-blue-700 border-blue-100'
                        }`}>
                          €{inv.amount}
                        </div>
                      ))}
                    </div>
                  )}
                  {isToday && (
                    <div className="absolute inset-0 border-2 border-slate-400 rounded pointer-events-none opacity-50"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Weekly Cash-In Projection (Chart) */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col">
          <div className="mb-6">
             <div className="flex items-center space-x-2 mb-1">
              <DollarSign size={18} className="text-slate-500" />
              <h2 className="text-lg font-semibold text-slate-800">Weekly Cash-In Projection</h2>
            </div>
            <p className="text-xs text-slate-500">Expected payments grouped by week</p>
          </div>
          
          <div className="flex-1 min-h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={CASHFLOW_DATA} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorCashIn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorExpected" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#94a3b8" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{fill: '#94a3b8', fontSize: 11}} 
                  dy={10} 
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                    borderRadius: '8px', 
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                  }}
                  itemStyle={{ fontSize: '12px', fontWeight: 500 }}
                  labelStyle={{ color: '#64748b', fontSize: '11px', marginBottom: '4px' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="expected" 
                  name="Projected"
                  stroke="#cbd5e1" 
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  fill="url(#colorExpected)" 
                />
                <Area 
                  type="monotone" 
                  dataKey="cashIn" 
                  name="Realized"
                  stroke="#10b981" 
                  strokeWidth={2}
                  fill="url(#colorCashIn)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CashflowForecast;